import { FinancialStatementPart } from '../../../models/common/FinancialStatementPart';
import { PeriodType, PeriodWithStartAndEnd } from '../../../utils/common/utilityClasses';


export class FsPeriodsFormatterService {
  /**
   * Returns an array of previous quarters for profit and loss calculation.
   * The number of quarters returned is determined by the `numberOfQuarters` parameter.
   * The quarters are based on the last present quarter obtained from the given `financialStatementParts`.
   *
   * @param financialStatementParts - The financial statement parts containing BS or P&L sums.
   * @param numberOfQuarters - The number of previous quarters to retrieve.
   * @returns An array of previous quarters in the format "QX YYYY" (e.g., "Q1 2021").
   */
  static getShownQuarters(financialStatementParts: FinancialStatementPart[], numberOfQuarters: number): string[] {
    const allExistingQuarters = this.getAllExistingPeriodsFromParts(financialStatementParts);
    const lastPresentQuarter = this.getLatestPresentPeriod(PeriodType.quarter, allExistingQuarters);

    let previousQuarters = [];
    if (!lastPresentQuarter) {
      return previousQuarters;
    }

    let year = parseInt(lastPresentQuarter.slice(3));
    const lastQuarter = lastPresentQuarter.slice(0,2);
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    let quarterIndex = quarters.indexOf(lastQuarter);

    previousQuarters.push(`${quarters[quarterIndex]} ${year}`);

    for (let i = 0; i < numberOfQuarters - 1; i++) {
      quarterIndex = quarterIndex - 1;
      if (quarterIndex < 0) {
        quarterIndex = 3;
        year -= 1;
      }
      previousQuarters.push(`${quarters[quarterIndex]} ${year}`);
    }

    previousQuarters = this.removeEmptyPeriodsUntilFirstPresentIsMet(previousQuarters.reverse(), allExistingQuarters);
    return previousQuarters;
  };

  /**
   * Returns an array of previous years for BS or P&L calculation.
   * The number of years returned is determined by the `numberOfYears` parameter.
   * The years are based on the last present year obtained from the given `financialStatementParts`.
   *
   * @param financialStatementParts - The financial statement parts containing BS or P&L sums.
   * @param numberOfYears - The number of previous years to retrieve.
   * @returns An array of previous years as strings (e.g., ["2018", "2019", "2020"]).
   */
  static getShownYears(financialStatementParts: FinancialStatementPart[], numberOfYears: number): string[] {
    const allExistingPeriods = this.getAllExistingPeriodsFromParts(financialStatementParts);
    const lastPresentPeriod = this.getLatestPresentPeriod(PeriodType.year, allExistingPeriods);
    let years: string[] = [];

    if (!lastPresentPeriod) {
      return years;
    }

    const currentYear = parseInt(lastPresentPeriod);

    for (let i = numberOfYears - 1; i >= 0; i--) {
      years.push((currentYear - i).toString());
    }

    years = this.removeEmptyPeriodsUntilFirstPresentIsMet(years, allExistingPeriods);
    return years;
  };

  /**
   * Returns the last present periodType based on the provided profit and loss quarterly sums
   * The periodType can be either a quarter or a year.
   * If both profitSums and lossSums are empty, null will be returned.
   *
   * @param periodType - The periodType type, either quarter or year.
   * @param combinedKeys - All existing quarterly keys (from FinancialStatementPart[])
   * @returns The last present periodType in the format "QX YYYY" (e.g., "Q1 2021") if periodType is 'quarter',
   *          or the last present year (e.g., "2021") if periodType is 'year'.
   */
  static getLatestPresentPeriod(periodType: PeriodType, combinedKeys: Set<string>): string {
    if (combinedKeys.size === 0) {
      return null;
    }
    const lastPresentQuarter = this.comparePresentQuartersAndGetLatest(combinedKeys);
    return periodType === PeriodType.year ? lastPresentQuarter.slice(3) : lastPresentQuarter;
  };

  static getAllExistingPeriodsFromParts(financialStatementParts: FinancialStatementPart[]): Set<string> {
    return new Set(financialStatementParts.flatMap((part) => Array.from(part.sums.quarterlySums.keys())));
  }

  /**
   * Converts an array of quarter strings to an array of objects representing the start and end dates of each quarter.
   *
   * @param quarters - An array of quarter strings (e.g., ["Q1 2021", "Q2 2021"]).
   * @returns An array of objects containing the start and end dates for each quarter.
   *          Each object has the format { start: "YYYY-MM-DD", end: "YYYY-MM-DD" }.
   */
  static formatQuartersWithStartAndEnd(quarters: string[]): PeriodWithStartAndEnd[] {
    const formattedQuarters: PeriodWithStartAndEnd[] = [];

    for (const quarter of quarters) {
      if (!QuarterRegex.test(quarter)) {
        continue;
      }
      const year = quarter.slice(3);
      const currentQuarter = quarter.slice(0, 2);
      const quarterStart = this.getQuarterStart(currentQuarter);
      const quarterEnd = this.getQuarterEnd(currentQuarter);

      formattedQuarters.push({
        start: year + '-' + quarterStart,
        end: year + '-' + quarterEnd,
      });
    }

    return formattedQuarters;
  };

  /**
   * Converts an array of year strings to an array of objects representing the start and end dates for each year.
   *
   * @param years - An array of year strings.
   * @param financialStatementPeriod - The financial statement period in the format "YYYY-MM-DD".
   * @returns An array of objects containing the start and end dates for each year.
   *          Each object has the format { start: "YYYY-01-01", end: "YYYY-12-31" }.
   */
  static formatYearsWithStartAndEnd(years: string[], financialStatementPeriod: string): PeriodWithStartAndEnd[] {
    const lastYear = financialStatementPeriod.slice(0,4);
    const financialStatementEnd = financialStatementPeriod.slice(5);
    const yearsHeaders: { start: string; end: string }[] = [];

    for (const year of years) {
      if (!YearRegex.test(year)) {
        continue;
      }
      const yearStart = '01-01';
      const yearEnd = year === lastYear ? financialStatementEnd : '12-31';

      yearsHeaders.push({
        start: year + '-' + yearStart,
        end: year + '-' + yearEnd,
      });
    }
    return yearsHeaders;
  };

  private static comparePresentQuartersAndGetLatest(combinedKeys: Set<string>): string {
    return [...combinedKeys.keys()].reduce((youngest, current) => {
      const [quarterYoungest, yearYoungest] = youngest.split(' ');
      const [quarterCurrent, yearCurrent] = current.split(' ');

      const yearComparison = parseInt(yearYoungest, 10) - parseInt(yearCurrent, 10);
      if (yearComparison !== 0) {
        return yearComparison > 0 ? youngest : current;
      }

      const quarterComparison = parseInt(quarterYoungest.slice(1), 10) - parseInt(quarterCurrent.slice(1), 10);
      return quarterComparison > 0 ? youngest : current;
    });
  }

  private static getQuarterStart(quarter: string): string {
    switch (quarter) {
      case 'Q1':
        return '01-01';
      case 'Q2':
        return '04-01';
      case 'Q3':
        return '07-01';
      case 'Q4':
        return '10-01';
      default:
        return '';
    }
  };

  private static getQuarterEnd(quarter: string): string {
    switch (quarter) {
      case 'Q1':
        return '03-31';
      case 'Q2':
        return '06-30';
      case 'Q3':
        return '09-30';
      case 'Q4':
        return '12-31';
      default:
        return '';
    }
  };


  private static removeEmptyPeriodsUntilFirstPresentIsMet(periodsList: string[], existingPeriods: Set<string>): string[] {
    for (let i = 0; i < periodsList.length; i++) {
      const period = periodsList[i];
      if ([...existingPeriods].some((key) => key.includes(period))) { // if statement handles both quarters and years
        periodsList = periodsList.slice(i); // remove empty periods until first present is met
        break;
      }
    }
    return periodsList;
  };

}

const QuarterRegex = /^Q[1-4] [0-9]{4}$/;
const YearRegex = /^[0-9]{4}$/;
